// =============================================================================
// DAMS — DASMO Asset Management System · Main Application
// Iloilo City Government · Cloudflare Pages + D1
// =============================================================================

import React, {
  useState, useEffect, useCallback, useRef, useMemo,
  DragEvent, ChangeEvent,
} from 'react';
import {
  Monitor, Package, Network, ShieldCheck, Database,
  RefreshCw, Upload, AlertTriangle, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, Search, Cpu, HardDrive,
  Wifi, Lock, TrendingUp, Activity, Building2, BarChart3,
  AlertOctagon, Printer, Smartphone, Box, Router, Download,
} from 'lucide-react';

import type {
  Computer, ComputerSoftware, Printer as PrinterT, MobileDevice,
  OtherEquipment, NetworkDevice, CitySystem,
  NetworkSurveyItem, SecuritySurveyItem, SyncLog,
  TabId, IsspDashboardMetrics, StorageMode, BulkUpsertPayload,
  DeptAssetSummary,
} from './types';
import { getRepository, getStorageMode, bulkSyncToD1 } from './storageRepo';
import { parseInventoryCSV, normalizeComputers, normalizeComputerSoftware,
  normalizePrinters, normalizeMobileDevices, normalizeOtherEquipment,
  normalizeNetworkDevices, normalizeSystems,
  normalizeNetworkSurvey, normalizeSecuritySurvey } from './csvParser';
import { computeIsspMetrics, getDeptAcronym, getDeptName, DEPARTMENT_MAP } from './isspAnalytics';
import {
  SEED_COMPUTERS, SEED_SOFTWARE, SEED_PRINTERS, SEED_MOBILE,
  SEED_EQUIPMENT, SEED_NET_DEVICES, SEED_SYSTEMS,
  SEED_NETWORK_SURVEY, SEED_SECURITY_SURVEY,
} from './seedData';

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
const PHP  = (n: number) =>
  new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', maximumFractionDigits: 0 }).format(n);
const PCT  = (n: number) => `${Math.round(n)}%`;
const clsx = (...c: (string|false|undefined|null)[]) => c.filter(Boolean).join(' ');
const uid  = () => `SYNC-${Date.now()}-${Math.random().toString(36).slice(2,8).toUpperCase()}`;

function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const esc  = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
  const body = [headers, ...rows].map(r => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Shared UI atoms
// ---------------------------------------------------------------------------
function Badge({ children, variant = 'default' }: { children: React.ReactNode; variant?: string }) {
  const s: Record<string, string> = {
    default: 'bg-gray-100 text-gray-700',
    success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-800',
    danger:  'bg-red-100 text-red-700',
    info:    'bg-blue-100 text-blue-700',
    purple:  'bg-purple-100 text-purple-700',
    orange:  'bg-orange-100 text-orange-700',
  };
  return (
    <span className={clsx('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', s[variant] ?? s.default)}>
      {children}
    </span>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color = 'blue' }: {
  label: string; value: string | number; sub?: string;
  icon: React.ElementType; color?: string;
}) {
  const g: Record<string, string> = {
    blue:   'from-blue-500 to-blue-700',
    green:  'from-emerald-500 to-emerald-700',
    amber:  'from-amber-500 to-amber-700',
    red:    'from-red-500 to-red-700',
    purple: 'from-purple-500 to-purple-700',
    teal:   'from-teal-500 to-teal-700',
    orange: 'from-orange-500 to-orange-700',
    slate:  'from-slate-500 to-slate-700',
  };
  return (
    <div className={clsx('rounded-xl p-5 text-white shadow-lg bg-gradient-to-br', g[color] ?? g.blue)}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest opacity-80">{label}</p>
          <p className="mt-1 text-3xl font-bold">{value}</p>
          {sub && <p className="mt-1 text-xs opacity-75">{sub}</p>}
        </div>
        <Icon className="h-8 w-8 opacity-70 shrink-0" />
      </div>
    </div>
  );
}

function Gauge({ pct, rating }: { pct: number; rating: string }) {
  const col: Record<string, string> = {
    Critical: '#dc2626', Low: '#f97316', Moderate: '#f59e0b', Good: '#10b981', Excellent: '#0d9488',
  };
  const textCol: Record<string, string> = {
    Critical: 'text-red-600', Low: 'text-orange-500', Moderate: 'text-amber-500',
    Good: 'text-emerald-500', Excellent: 'text-teal-600',
  };
  const r = 52; const circ = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center">
      <svg width="136" height="136" viewBox="0 0 136 136">
        <circle cx="68" cy="68" r={r} fill="none" stroke="#e5e7eb" strokeWidth="12" />
        <circle cx="68" cy="68" r={r} fill="none" stroke={col[rating] ?? '#6b7280'} strokeWidth="12"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)}
          strokeLinecap="round" transform="rotate(-90 68 68)"
          style={{ transition: 'stroke-dashoffset .6s ease' }} />
        <text x="68" y="63" textAnchor="middle" fontSize="22" fontWeight="700" fill="currentColor">{pct}%</text>
        <text x="68" y="80" textAnchor="middle" fontSize="10" fill="#6b7280">Readiness</text>
      </svg>
      <span className={clsx('mt-1 text-sm font-semibold', textCol[rating] ?? 'text-gray-600')}>{rating}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500">{children}</h3>;
}

// ---------------------------------------------------------------------------
// Mini stat strip — used as a mini dashboard at the top of each tab
// ---------------------------------------------------------------------------
type StatColor = 'default'|'red'|'green'|'amber'|'blue'|'purple'|'slate';
function StatStrip({ stats }: {
  stats: { label: string; value: string|number; color?: StatColor; sub?: string }[]
}) {
  const bg: Record<StatColor, string> = {
    default: 'bg-white border-gray-200 text-gray-800',
    red:     'bg-red-50 border-red-200 text-red-800',
    green:   'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber:   'bg-amber-50 border-amber-200 text-amber-800',
    blue:    'bg-blue-50 border-blue-200 text-blue-800',
    purple:  'bg-purple-50 border-purple-200 text-purple-800',
    slate:   'bg-slate-50 border-slate-200 text-slate-800',
  };
  return (
    <div className="flex flex-wrap gap-2.5">
      {stats.map(s => (
        <div key={s.label} className={clsx('flex flex-col rounded-xl border px-4 py-3 shadow-sm min-w-[90px]', bg[s.color ?? 'default'])}>
          <span className="text-2xl font-bold leading-none tabular-nums">{s.value}</span>
          <span className="mt-1.5 text-[11px] font-medium uppercase tracking-wide opacity-60">{s.label}</span>
          {s.sub && <span className="text-[10px] opacity-45 mt-0.5">{s.sub}</span>}
        </div>
      ))}
    </div>
  );
}

// DICT data-completeness helpers
function osIsWin11(os: string): boolean {
  const l = os.toLowerCase().replace(/[\s\-_.]/g, '');
  return l.startsWith('win') && l.includes('11');
}
function computerComplete(c: Computer): boolean {
  return !!c.brand_serial && !!c.os && c.year_acquired > 0 && c.dept_id !== 999;
}
function completePct(total: number, complete: number): string {
  return total === 0 ? 'N/A' : `${Math.round(complete / total * 100)}%`;
}

function EmptyRow({ cols }: { cols: number }) {
  return <tr><td colSpan={cols} className="py-10 text-center text-sm text-gray-400">No records loaded</td></tr>;
}

// ---------------------------------------------------------------------------
// Shared chart components
// ---------------------------------------------------------------------------
function HBar({ label, count, total, color, isEol }: { label: string; count: number; total: number; color: string; isEol?: boolean }) {
  const pct = total > 0 ? count / total * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className={clsx('w-28 shrink-0 text-xs truncate', isEol ? 'text-red-600 font-medium' : 'text-gray-700')} title={label}>{label}</span>
      <div className="flex-1 rounded-full bg-gray-100 h-2.5 overflow-hidden">
        <div className="h-2.5 rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="w-16 shrink-0 text-right text-xs font-semibold tabular-nums text-gray-700">{count} <span className="font-normal text-gray-400">({Math.round(pct)}%)</span></span>
    </div>
  );
}

function AgeBar({ groups }: { groups: { label: string; count: number; isEol: boolean }[] }) {
  const max = Math.max(...groups.map(g => g.count), 1);
  return (
    <div className="flex items-end gap-2 h-24 pt-2">
      {groups.filter(g => g.count > 0 || g.label !== 'No Year').map(g => {
        const h = Math.max(4, g.count / max * 88);
        return (
          <div key={g.label} className="flex flex-col items-center flex-1 gap-1">
            <span className="text-[10px] font-semibold tabular-nums text-gray-600">{g.count > 0 ? g.count : ''}</span>
            <div className="w-full rounded-t" style={{ height: h, background: g.isEol ? '#f87171' : '#34d399' }} title={`${g.label}: ${g.count}`} />
            <span className="text-[9px] text-gray-400 text-center leading-tight">{g.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: Executive Dashboard
// ---------------------------------------------------------------------------
function DashboardTab({ m, syncLogs, onGoToSync }: { m: IsspDashboardMetrics; syncLogs: SyncLog[]; onGoToSync: () => void }) {
  const [refreshPct, setRefreshPct] = useState<10|50|100>(10);
  const [deptSort, setDeptSort] = useState<'total'|'eolPct'|'win11Pct'>('eolPct');
  const trend = useMemo(() => {
    const sorted = [...syncLogs].sort((a, b) => b.synced_at.localeCompare(a.synced_at));
    if (sorted.length < 2) return null;
    const [latest, prev] = sorted;
    return {
      eolDelta:   latest.eol_count - prev.eol_count,
      win11Delta: latest.win11_count - prev.win11_count,
      prevDate:   prev.synced_at,
    };
  }, [syncLogs]);
  const maxClusterOpex = Math.max(1, ...m.opexByCluster.map(c => c.annual_opex));
  const heatDepts = useMemo(() =>
    m.deptSummary.filter(d => d.dept_id !== 999 && d.total > 0),
  [m.deptSummary]);
  const maxHeatTotal = Math.max(1, ...heatDepts.map(d => d.total));
  const topUnlicensedDepts = useMemo(() =>
    [...m.deptSummary].filter(d => d.unlicensedSw > 0).sort((a, b) => b.unlicensedSw - a.unlicensedSw).slice(0, 5),
  [m.deptSummary]);
  const maxUnlicensed = Math.max(1, ...topUnlicensedDepts.map(d => d.unlicensedSw));

  const exportIsspSummary = () => downloadCsv(
    `dams-issp-summary-${new Date().toISOString().slice(0,10)}.csv`,
    ['Dept ID', 'Acronym', 'Department', 'Cluster', 'Computers', 'Win11 Count', 'Win11 %', 'EOL Computers', 'EOL %', 'Printers', 'Mobile', 'Equipment', 'Net Devices', 'Systems', 'Total Assets', 'Unlicensed Software', 'Annual OPEX (PHP)'],
    [...m.deptSummary].filter(d => d.dept_id !== 999).sort((a, b) => a.dept_id - b.dept_id).map(d => [
      d.dept_id, d.acronym, d.dept_name, d.cluster, d.computers, d.win11Count, d.win11Pct,
      d.eolComputers, d.eolPct, d.printers, d.mobile, d.equipment, d.netDevices, d.systems,
      d.total, d.unlicensedSw, d.totalOpex,
    ]),
  );

  const exportRefreshQueue = () => downloadCsv(
    `dams-refresh-priority-queue-${new Date().toISOString().slice(0,10)}.csv`,
    ['Rank', 'Computer ID', 'Brand / Serial', 'OS', 'Year Acquired', 'Age (yrs)', 'Department', 'Status', 'Remarks'],
    m.refreshQueue.map((q, i) => [
      i + 1, q.computer_id, q.brand_serial, q.os,
      q.year_acquired || 'N/A', q.age >= 0 ? q.age : 'N/A',
      `${q.dept_acronym} — ${q.dept_name}`, q.status, q.remarks,
    ]),
  );

  const [clusterFilter, setClusterFilter] = useState('');
  const swTotal = m.license.licensed + m.license.oem + m.license.unlicensed + m.license.subscription + m.license.unknown;
  const clusterList = useMemo(() =>
    Array.from(new Set(m.deptSummary.filter(d => d.dept_id !== 999).map(d => d.cluster))).sort(),
  [m.deptSummary]);
  const topDepts = useMemo(() =>
    [...m.deptSummary]
      .filter(d => d.dept_id !== 999 && d.computers > 0)
      .filter(d => !clusterFilter || d.cluster === clusterFilter)
      .sort((a, b) => deptSort === 'total' ? b.total - a.total : deptSort === 'eolPct' ? b.eolPct - a.eolPct : a.win11Pct - b.win11Pct)
      .slice(0, 10),
  [m.deptSummary, deptSort, clusterFilter]);

  if (m.totalFleet === 0) return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-white py-20 text-center">
      <Upload className="h-10 w-10 text-blue-300" />
      <h3 className="mt-4 text-lg font-semibold text-gray-800">No inventory data yet</h3>
      <p className="mt-1.5 max-w-sm text-sm text-gray-500">
        Upload the DASMO inventory CSV to populate fleet KPIs, EOL tracking, and DICT ISSP compliance metrics.
      </p>
      <button onClick={onGoToSync}
        className="mt-5 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-blue-700">
        <Upload className="h-4 w-4" /> Go to Database &amp; Sync
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">ISSP Compliance Dashboard</h2>
        <button onClick={exportIsspSummary} disabled={m.deptSummary.length === 0}
          className="flex items-center gap-1.5 rounded-lg border border-blue-500 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-all hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed">
          <Download className="h-3.5 w-3.5" /> Export ISSP Summary (CSV)
        </button>
      </div>

      {/* KPI row 1 — Fleet */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
        <KpiCard label="Total Fleet"     value={m.totalFleet}      icon={HardDrive}  color="blue"   sub="All tracked assets" />
        <KpiCard label="Computers"       value={m.totalComputers}  icon={Cpu}        color="teal"   sub={`${m.activeComputers} active`} />
        <KpiCard label="Printers"        value={m.totalPrinters}   icon={Printer}    color="slate"  sub="Print / scan devices" />
        <KpiCard label="Other Equipment" value={m.totalEquipment}  icon={Box}        color="amber"  sub="Monitors, UPS, etc." />
        <KpiCard label="Mobile / Net"    value={m.totalMobile + m.totalNetDevices} icon={Smartphone} color="purple" sub="Mobile + network" />
      </div>

      {/* KPI row 2 — ISSP compliance */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KpiCard label="Win11 Compliant" value={`${m.win11Count} (${PCT(m.win11Pct)})`} icon={CheckCircle} color="green"
          sub={trend ? `${trend.win11Delta >= 0 ? '+' : ''}${trend.win11Delta} since last upload` : 'OS-level ISSP compliance'} />
        <KpiCard label="EOL Computers"   value={m.eol.eolCount} icon={AlertTriangle} color="red"
          sub={trend ? `${trend.eolDelta >= 0 ? '+' : ''}${trend.eolDelta} since last upload` : `${PCT(m.eol.eolPercentage)} — Win10 & below`} />
        <KpiCard label="Annual OPEX"     value={PHP(m.totalOpex)} icon={TrendingUp}  color="purple" sub="System maintenance cost" />
        <KpiCard label="Cyber Readiness" value={PCT(m.cyber.percentage)} icon={Lock} color={m.cyber.percentage >= 70 ? 'teal' : m.cyber.percentage >= 50 ? 'amber' : 'red'} sub={m.cyber.rating} />
      </div>

      {/* Row 2b — Department EOL Risk Heat Map */}
      <div className="rounded-xl border bg-white p-6 shadow-sm">
        <SectionTitle>Department EOL Risk Map</SectionTitle>
        <p className="text-xs text-gray-400 mb-3">Bubble size = total assets · position: right &amp; up = large fleet, high EOL risk = refresh first</p>
        {heatDepts.length === 0 ? <p className="text-sm text-gray-400">No department data available.</p> : (
          <svg viewBox="0 0 420 220" className="w-full h-56">
            <line x1="40" y1="10" x2="40" y2="190" stroke="#e5e7eb" strokeWidth="1" />
            <line x1="40" y1="190" x2="410" y2="190" stroke="#e5e7eb" strokeWidth="1" />
            {[0, 25, 50, 75, 100].map(p => (
              <text key={p} x="34" y={190 - p * 1.7} textAnchor="end" fontSize="8" fill="#9ca3af">{p}%</text>
            ))}
            <text x="4" y="15" fontSize="8" fill="#9ca3af" transform="rotate(-90 4 15)">EOL %</text>
            <text x="410" y="204" textAnchor="end" fontSize="8" fill="#9ca3af">Total Assets →</text>
            {heatDepts.map(d => {
              const x = 40 + (d.total / maxHeatTotal) * 360;
              const y = 190 - (d.eolPct / 100) * 170;
              const r = Math.max(4, Math.min(16, 4 + d.total / maxHeatTotal * 14));
              const color = d.eolPct >= 80 ? '#ef4444' : d.eolPct >= 50 ? '#f59e0b' : '#10b981';
              return (
                <g key={d.dept_id}>
                  <circle cx={x} cy={y} r={r} fill={color} fillOpacity={0.55} stroke={color} strokeWidth="1.5" />
                  <title>{`${d.acronym} — ${d.dept_name}\nAssets: ${d.total} · EOL: ${d.eolPct}%`}</title>
                  {r >= 9 && <text x={x} y={y + 3} textAnchor="middle" fontSize="7" fill="#1f2937" fontWeight="600">{d.acronym}</text>}
                </g>
              );
            })}
          </svg>
        )}
        <div className="mt-2 flex items-center gap-4 border-t pt-3 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-400" />Low risk (&lt;50% EOL)</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" />Moderate (50–79%)</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-red-400" />Critical (≥80%)</span>
        </div>
      </div>

      {/* Row 2c — Windows 11 Upgrade Funnel */}
      <div className="rounded-xl border bg-white p-6 shadow-sm">
        <SectionTitle>Windows 11 Upgrade Funnel</SectionTitle>
        <p className="text-xs text-gray-400 mb-4">Splits non-Win11 units into "free win" upgrades vs. units needing hardware replacement (year ≥ {2018} used as a TPM 2.0 eligibility proxy)</p>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-4">
            <div className="text-2xl font-bold text-gray-800 tabular-nums">{m.win11Funnel.total}</div>
            <div className="text-xs text-gray-500 mt-1">Total Computers</div>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-4">
            <div className="text-2xl font-bold text-blue-700 tabular-nums">{m.win11Funnel.upgradeable}</div>
            <div className="text-xs text-blue-600 mt-1">Upgradeable (free win)</div>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-4">
            <div className="text-2xl font-bold text-red-700 tabular-nums">{m.win11Funnel.mustReplace}</div>
            <div className="text-xs text-red-600 mt-1">Needs Hardware Replacement</div>
          </div>
        </div>
        <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-gray-100 flex">
          <div className="h-3 bg-emerald-400" style={{ width: `${m.win11Funnel.total ? m.win11Funnel.win11 / m.win11Funnel.total * 100 : 0}%` }} title="Already Win11" />
          <div className="h-3 bg-blue-400"    style={{ width: `${m.win11Funnel.total ? m.win11Funnel.upgradeable / m.win11Funnel.total * 100 : 0}%` }} title="Upgradeable" />
          <div className="h-3 bg-red-400"     style={{ width: `${m.win11Funnel.total ? m.win11Funnel.mustReplace / m.win11Funnel.total * 100 : 0}%` }} title="Must Replace" />
        </div>
        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-400" />Win11 ({m.win11Funnel.win11})</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-400" />Upgradeable ({m.win11Funnel.upgradeable})</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-red-400" />Replace ({m.win11Funnel.mustReplace})</span>
        </div>
      </div>

      {/* Row 3 — OS Distribution + Age Distribution */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border bg-white p-6 shadow-sm">
          <SectionTitle>OS Migration Status</SectionTitle>
          <p className="text-xs text-gray-400 mb-4">Windows 11 = compliant · Win10 &amp; below = EOL per DICT ISSP</p>
          {m.osDistribution.slice(0, 8).map(g => (
            <HBar key={g.os} label={g.label} count={g.count} total={m.totalComputers}
              color={g.os === 'win11' ? '#10b981' : g.os === 'win10' ? '#f59e0b' : g.isEol ? '#ef4444' : '#94a3b8'}
              isEol={g.isEol} />
          ))}
          <div className="mt-4 flex items-center gap-4 border-t pt-3 text-xs text-gray-500">
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-400" />Win11 (compliant)</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" />Win10 (EOL)</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-red-400" />Win7/8 (critical)</span>
          </div>
        </div>

        <div className="rounded-xl border bg-white p-6 shadow-sm">
          <SectionTitle>Asset Age Distribution (Computers)</SectionTitle>
          <p className="text-xs text-gray-400 mb-3">Green = within lifecycle · Red = past 5-year threshold</p>
          <AgeBar groups={m.ageDistribution} />
          <div className="mt-4 grid grid-cols-3 gap-2 border-t pt-3">
            {m.ageDistribution.filter(g => g.label !== 'No Year').map(g => (
              <div key={g.range} className={clsx('rounded-lg px-3 py-2 text-center text-xs', g.isEol ? 'bg-red-50 border border-red-100' : 'bg-emerald-50 border border-emerald-100')}>
                <div className={clsx('text-lg font-bold tabular-nums', g.isEol ? 'text-red-600' : 'text-emerald-600')}>{g.count}</div>
                <div className={clsx('opacity-60', g.isEol ? 'text-red-700' : 'text-emerald-700')}>{g.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 4 — Department EOL Priority Table */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="font-semibold text-gray-800">Department Compliance Ranking</h3>
            <p className="text-xs text-gray-500 mt-0.5">Top 10 departments — prioritize for hardware refresh procurement</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={clusterFilter} onChange={e => setClusterFilter(e.target.value)}
              className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
              <option value="">All Clusters</option>
              {clusterList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex gap-1">
            {(['eolPct', 'win11Pct', 'total'] as const).map(s => (
              <button key={s} onClick={() => setDeptSort(s)}
                className={clsx('rounded-lg border px-3 py-1.5 text-xs font-medium transition-all',
                  deptSort === s ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50')}>
                {s === 'eolPct' ? 'By EOL %' : s === 'win11Pct' ? 'By Win11 %' : 'By Total'}
              </button>
            ))}
            </div>
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">#</th>
              <th className="px-4 py-3 text-left font-semibold">Department</th>
              <th className="px-4 py-3 text-left font-semibold">Cluster</th>
              <th className="px-4 py-3 text-right font-semibold">Computers</th>
              <th className="px-4 py-3 text-right font-semibold">Win11</th>
              <th className="px-4 py-3 text-right font-semibold">EOL</th>
              <th className="px-4 py-3 text-left font-semibold w-40">OS Compliance</th>
              <th className="px-4 py-3 text-right font-semibold">Assets</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {topDepts.length === 0 && <EmptyRow cols={8} />}
            {topDepts.map((d, i) => (
              <tr key={d.dept_id} className={clsx('hover:bg-gray-50', d.eolPct >= 80 && 'bg-red-50/30')}>
                <td className="px-4 py-2.5 text-xs text-gray-400 font-medium">{i + 1}</td>
                <td className="px-4 py-2.5">
                  <span className="font-semibold text-gray-800 text-xs">{d.acronym}</span>
                  <span className="block text-[11px] text-gray-400 truncate max-w-[180px]" title={d.dept_name}>{d.dept_name}</span>
                </td>
                <td className="px-4 py-2.5"><Badge variant="info">{d.cluster}</Badge></td>
                <td className="px-4 py-2.5 text-right text-xs font-medium tabular-nums">{d.computers}</td>
                <td className="px-4 py-2.5 text-right text-xs font-semibold text-emerald-600 tabular-nums">{d.win11Count} <span className="text-gray-400 font-normal">({d.win11Pct}%)</span></td>
                <td className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums">
                  <span className={d.eolComputers > 0 ? 'text-red-600' : 'text-gray-400'}>{d.eolComputers} <span className="font-normal">({d.eolPct}%)</span></span>
                </td>
                <td className="px-4 py-2.5 w-40">
                  <div className="flex-1 rounded-full bg-gray-100 h-1.5 overflow-hidden">
                    <div className="h-1.5 rounded-full bg-emerald-400 transition-all" style={{ width: `${d.win11Pct}%` }} />
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right text-xs text-gray-600 tabular-nums">{d.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Row 5 — DICT Gauge + License + Unlicensed SW + EOL Simulator */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border bg-white p-6 shadow-sm">
          <SectionTitle>DICT Cyber Readiness</SectionTitle>
          <Gauge pct={m.cyber.percentage} rating={m.cyber.rating} />
          <ul className="mt-4 space-y-1.5">
            {m.cyber.indicators.map(ind => (
              <li key={ind.label} className="flex items-center gap-2 text-xs text-gray-600">
                {ind.present
                  ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  : <XCircle     className="h-3.5 w-3.5 text-red-400    shrink-0" />}
                <span className={ind.present ? '' : 'text-red-500 font-medium'}>{ind.label}</span>
                <span className="ml-auto text-gray-400">({ind.weight}pts)</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border bg-white p-6 shadow-sm">
          <SectionTitle>Software License Audit</SectionTitle>
          {[
            { label: 'Licensed',       n: m.license.licensed,     color: '#10b981' },
            { label: 'OEM',            n: m.license.oem,          color: '#3b82f6' },
            { label: 'Unlicensed ⚠',   n: m.license.unlicensed,   color: '#ef4444' },
            { label: 'Subscription ⚠', n: m.license.subscription, color: '#f59e0b' },
            { label: 'Unknown ⚠',      n: m.license.unknown,      color: '#f97316' },
          ].map(({ label, n, color }) => (
            <HBar key={label} label={label} count={n} total={swTotal} color={color} />
          ))}
          {(m.license.unlicensed + m.license.subscription) > 0 && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 border border-red-200">
              <AlertOctagon className="inline h-3.5 w-3.5 mr-1" />
              {m.license.unlicensed + m.license.subscription} entries need immediate audit
            </div>
          )}
        </div>

        <div className="rounded-xl border bg-white p-6 shadow-sm">
          <SectionTitle>Top Departments — Unlicensed Software</SectionTitle>
          <p className="text-xs text-gray-400 mb-3">Priority targets for software license audits</p>
          {topUnlicensedDepts.length === 0 && <p className="text-sm text-gray-400">No unlicensed software entries found.</p>}
          {topUnlicensedDepts.map(d => (
            <div key={d.dept_id} className="mb-2.5">
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="font-medium text-gray-700">{d.acronym} <span className="text-gray-400 font-normal">{d.dept_name}</span></span>
                <span className="font-semibold text-red-600 tabular-nums">{d.unlicensedSw}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div className="h-2 rounded-full bg-red-400" style={{ width: `${(d.unlicensedSw / maxUnlicensed) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border bg-white p-6 shadow-sm">
          <SectionTitle>EOL Refresh Budget Simulator</SectionTitle>
          <p className="text-xs text-gray-500 mb-3">{m.eol.eolCount} EOL units × ₱45,000 avg. replacement</p>
          <div className="space-y-2 mb-4">
            {([10, 50, 100] as const).map(pct => (
              <button key={pct} onClick={() => setRefreshPct(pct)}
                className={clsx('w-full rounded-lg border px-3 py-2 text-left text-xs font-medium transition-all',
                  refreshPct === pct ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50')}>
                Refresh {pct}% of EOL computers ({Math.ceil(m.eol.eolCount * pct / 100)} units)
              </button>
            ))}
          </div>
          <div className="rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 p-4 text-white">
            <p className="text-xs opacity-75">Projected Budget Requirement</p>
            <p className="mt-1 text-2xl font-bold">{PHP(m.eol.refreshCostProjection[refreshPct] ?? 0)}</p>
            <p className="text-xs opacity-60 mt-1">For {Math.ceil(m.eol.eolCount * refreshPct / 100)} replacement units</p>
          </div>
        </div>
      </div>

      {/* Row 6 — OPEX by Cluster */}
      <div className="rounded-xl border bg-white p-6 shadow-sm">
        <SectionTitle>Annual OPEX by Department Cluster</SectionTitle>
        <p className="text-xs text-gray-400 mb-4">System maintenance spend rolled up by cluster — {PHP(m.totalOpex)} citywide</p>
        {m.opexByCluster.length === 0 && <p className="text-sm text-gray-400">No OPEX data available.</p>}
        {m.opexByCluster.map(c => (
          <div key={c.cluster} className="mb-2.5">
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium text-gray-700">{c.cluster} <span className="text-gray-400 font-normal">({c.dept_count} dept{c.dept_count !== 1 ? 's' : ''}, {c.system_count} systems)</span></span>
              <span className="font-semibold text-purple-700 tabular-nums">{PHP(c.annual_opex)}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
              <div className="h-2 rounded-full bg-purple-500 transition-all" style={{ width: `${(c.annual_opex / maxClusterOpex) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Row 7 — Refresh Priority Queue */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="font-semibold text-gray-800">Refresh Priority Queue</h3>
            <p className="text-xs text-gray-500 mt-0.5">EOL computers ranked oldest-first — ready for BAC procurement submission</p>
          </div>
          <button onClick={exportRefreshQueue} disabled={m.refreshQueue.length === 0}
            className="flex items-center gap-1.5 rounded-lg border border-blue-500 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-all hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed">
            <Download className="h-3.5 w-3.5" /> Export CSV ({m.refreshQueue.length})
          </button>
        </div>
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">#</th>
              <th className="px-4 py-3 text-left font-semibold">Computer ID</th>
              <th className="px-4 py-3 text-left font-semibold">OS</th>
              <th className="px-4 py-3 text-right font-semibold">Age</th>
              <th className="px-4 py-3 text-left font-semibold">Department</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {m.refreshQueue.length === 0 && <EmptyRow cols={6} />}
            {m.refreshQueue.slice(0, 15).map((q, i) => (
              <tr key={q.computer_id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-xs text-gray-400 font-medium">{i + 1}</td>
                <td className="px-4 py-2.5 text-xs font-semibold text-gray-800">{q.computer_id}</td>
                <td className="px-4 py-2.5 text-xs text-gray-600">{q.os}</td>
                <td className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums text-red-600">{q.age >= 0 ? `${q.age} yrs` : 'N/A'}</td>
                <td className="px-4 py-2.5 text-xs">
                  <span className="font-semibold text-gray-800">{q.dept_acronym}</span>
                  <span className="ml-1 text-gray-400">{q.dept_name}</span>
                </td>
                <td className="px-4 py-2.5"><Badge variant={q.status === 'Active' ? 'info' : 'warning'}>{q.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
        {m.refreshQueue.length > 15 && (
          <div className="border-t bg-gray-50 px-6 py-2.5 text-center text-xs text-gray-500">
            + {m.refreshQueue.length - 15} more units — export CSV for the full list
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2: Computer Registry
// ---------------------------------------------------------------------------
const PAGE = 15;

function ComputersTab({ computers }: { computers: Computer[] }) {
  const [q, setQ] = useState('');
  const [filterStatus, setFS] = useState('');
  const [filterEol, setFE] = useState('');
  const [filterOs, setFO] = useState('');
  const [filterDept, setFD] = useState('');
  const [page, setPage] = useState(1);

  // Mini dashboard metrics (all computers, ignoring current filter)
  const win11Count  = useMemo(() => computers.filter(c => osIsWin11(c.os)).length, [computers]);
  const eolCount    = useMemo(() => computers.filter(c => c.eol_flag).length, [computers]);
  const activeCount = useMemo(() => computers.filter(c => c.status === 'Active').length, [computers]);
  const defCount    = useMemo(() => computers.filter(c => c.status === 'Defective' || c.status === 'For Disposal').length, [computers]);
  const complete    = useMemo(() => computers.filter(computerComplete).length, [computers]);

  const uniqueOsList = useMemo(() => Array.from(new Set(computers.map(c => c.os.toLowerCase().trim()))).filter(Boolean).sort(), [computers]);
  const deptList = useMemo(() => {
    const ids = Array.from(new Set(computers.map(c => c.dept_id))).sort((a, b) => a - b);
    return ids.map(id => ({ id, label: getDeptAcronym(id) }));
  }, [computers]);

  const filtered = useMemo(() => computers.filter(c => {
    if (q) {
      const lq = q.toLowerCase();
      if (!c.computer_id.toLowerCase().includes(lq) &&
          !c.brand_serial.toLowerCase().includes(lq) &&
          !c.remarks.toLowerCase().includes(lq) &&
          !c.os.toLowerCase().includes(lq) &&
          !getDeptAcronym(c.dept_id).toLowerCase().includes(lq)) return false;
    }
    if (filterStatus && c.status !== filterStatus) return false;
    if (filterEol !== '') {
      if (c.eol_flag !== (filterEol === 'true')) return false;
    }
    if (filterOs && c.os.toLowerCase().trim() !== filterOs) return false;
    if (filterDept && c.dept_id !== parseInt(filterDept)) return false;
    return true;
  }), [computers, q, filterStatus, filterEol, filterOs, filterDept]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const page_ = Math.min(page, totalPages);
  const rows = filtered.slice((page_ - 1) * PAGE, page_ * PAGE);

  const licBadge = (l: string) => {
    const lc = l.toLowerCase();
    if (lc === 'unlicensed' || lc === 'unknown') return 'danger';
    if (lc === 'oem' || lc === 'licensed' || lc === 'activated') return 'success';
    return 'warning';
  };

  return (
    <div className="space-y-4">
      {/* Mini dashboard */}
      <StatStrip stats={[
        { label: 'Total Computers', value: computers.length, color: 'blue' },
        { label: 'Active',          value: activeCount,      color: 'green' },
        { label: 'Win11 Valid',     value: win11Count,       color: 'green',  sub: `${PCT(computers.length ? win11Count/computers.length*100 : 0)} of fleet` },
        { label: 'EOL (Win10 & below)', value: eolCount,    color: eolCount > 0 ? 'red' : 'green', sub: `${PCT(computers.length ? eolCount/computers.length*100 : 0)} of fleet` },
        { label: 'Defective / Disposal', value: defCount,   color: defCount > 0 ? 'amber' : 'default' },
        { label: 'DICT Data Completeness', value: completePct(computers.length, complete), color: complete/Math.max(computers.length,1) >= 0.8 ? 'green' : 'amber', sub: `${complete}/${computers.length} records complete` },
      ]} />

      <div className="flex flex-wrap gap-3 rounded-xl border bg-white p-4 shadow-sm">
        <div className="relative flex-1 min-w-40">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Search ID, serial, OS, remarks…" value={q}
            onChange={e => { setQ(e.target.value); setPage(1); }} />
        </div>
        <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          value={filterStatus} onChange={e => { setFS(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          {['Active','Inactive','Defective','For Disposal','For Repair'].map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          value={filterOs} onChange={e => { setFO(e.target.value); setPage(1); }}>
          <option value="">All OS</option>
          {uniqueOsList.map(os => <option key={os} value={os}>{os}</option>)}
        </select>
        <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          value={filterDept} onChange={e => { setFD(e.target.value); setPage(1); }}>
          <option value="">All Depts</option>
          {deptList.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
        <select className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          value={filterEol} onChange={e => { setFE(e.target.value); setPage(1); }}>
          <option value="">EOL: All</option>
          <option value="true">EOL Only</option>
          <option value="false">Non-EOL</option>
        </select>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>Showing <strong>{rows.length}</strong> of <strong>{filtered.length}</strong></span>
        <div className="flex items-center gap-2">
          <button disabled={page_ <= 1} onClick={() => setPage(p => p - 1)}
            className="rounded border p-1 disabled:opacity-30 hover:bg-gray-50"><ChevronLeft className="h-4 w-4" /></button>
          <span>Page {page_} / {totalPages}</span>
          <button disabled={page_ >= totalPages} onClick={() => setPage(p => p + 1)}
            className="rounded border p-1 disabled:opacity-30 hover:bg-gray-50"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>{['ID','Brand / Serial','Type','Year','OS / License','Status','Dept','EOL'].map(h =>
              <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.length === 0 && <EmptyRow cols={8} />}
            {rows.map(c => (
              <tr key={c.computer_id} className={clsx('hover:bg-gray-50', c.eol_flag && 'bg-red-50/40')}>
                <td className="px-4 py-2.5 font-mono text-xs text-blue-600">{c.computer_id}</td>
                <td className="px-4 py-2.5 text-gray-700 max-w-[160px] truncate" title={c.brand_serial}>{c.brand_serial}</td>
                <td className="px-4 py-2.5"><Badge variant="info">{c.type}</Badge></td>
                <td className="px-4 py-2.5 text-gray-600">{c.year_acquired || '—'}</td>
                <td className="px-4 py-2.5 text-xs">
                  <Badge variant={osIsWin11(c.os) ? 'success' : c.eol_flag ? 'danger' : 'warning'}>
                    {c.os || 'Unknown'}
                  </Badge>
                  <span className="block mt-0.5"><Badge variant={licBadge(c.os_license)}>{c.os_license}</Badge></span>
                </td>
                <td className="px-4 py-2.5">
                  <Badge variant={c.status === 'Active' ? 'success' : c.status === 'Defective' ? 'danger' : 'warning'}>{c.status}</Badge>
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-500" title={getDeptName(c.dept_id)}>{getDeptAcronym(c.dept_id)}</td>
                <td className="px-4 py-2.5">
                  {c.eol_flag
                    ? <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600"><AlertTriangle className="h-3.5 w-3.5" />EOL</span>
                    : <span className="text-xs text-emerald-500">OK</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3: Other Assets (Printers / Mobile / Equipment / Network Devices)
// ---------------------------------------------------------------------------
type AssetSubTab = 'printers' | 'mobile' | 'equipment' | 'network';

function OtherAssetsTab({ printers, mobile, equipment, netDevices }: {
  printers: PrinterT[]; mobile: MobileDevice[];
  equipment: OtherEquipment[]; netDevices: NetworkDevice[];
}) {
  const [sub, setSub] = useState<AssetSubTab>('printers');

  const subTabs: { id: AssetSubTab; label: string; count: number; icon: React.ElementType }[] = [
    { id: 'printers',  label: 'Printers',        count: printers.length,   icon: Printer },
    { id: 'mobile',    label: 'Mobile Devices',   count: mobile.length,     icon: Smartphone },
    { id: 'equipment', label: 'Other Equipment',  count: equipment.length,  icon: Box },
    { id: 'network',   label: 'Network Devices',  count: netDevices.length, icon: Router },
  ];

  function GenericTable<T extends Record<string, unknown>>({
    rows, headers,
  }: { rows: T[]; headers: { key: string; label: string }[] }) {
    return (
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>{headers.map(h => <th key={h.key} className="px-4 py-3 text-left font-semibold">{h.label}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.length === 0 && <EmptyRow cols={headers.length} />}
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {headers.map(h => (
                  <td key={h.key} className="px-4 py-2.5 text-xs text-gray-700 max-w-[180px] truncate">
                    {h.key === 'status'
                      ? <Badge variant={String(r[h.key]) === 'Active' ? 'success' : String(r[h.key]) === 'Defective' ? 'danger' : 'warning'}>{String(r[h.key] ?? '—')}</Badge>
                      : h.key === 'eol_flag'
                        ? r[h.key] ? <span className="text-red-500 font-medium">EOL</span> : <span className="text-emerald-500">OK</span>
                        : String(r[h.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const subStats: Record<AssetSubTab, { label: string; value: string|number; color?: StatColor; sub?: string }[]> = {
    printers: [
      { label: 'Total Printers',   value: printers.length, color: 'blue' },
      { label: 'Active',           value: printers.filter(p => p.status === 'Active').length, color: 'green' },
      { label: 'Inactive',         value: printers.filter(p => p.status === 'Inactive').length, color: 'amber' },
      { label: 'EOL (5+ yrs)',     value: printers.filter(p => p.eol_flag).length, color: printers.some(p => p.eol_flag) ? 'red' : 'default' },
      { label: 'Data Complete',    value: completePct(printers.length, printers.filter(p => !!p.brand_serial && p.year_acquired > 0 && p.dept_id !== 999).length), color: 'slate' },
    ],
    mobile: [
      { label: 'Total Mobile',     value: mobile.length, color: 'blue' },
      { label: 'Active',           value: mobile.filter(m => m.status === 'Active').length, color: 'green' },
      { label: 'Inactive',         value: mobile.filter(m => m.status === 'Inactive').length, color: 'amber' },
      { label: 'Data Complete',    value: completePct(mobile.length, mobile.filter(m => !!m.brand_serial && m.year_acquired > 0 && m.dept_id !== 999).length), color: 'slate' },
    ],
    equipment: [
      { label: 'Total Equipment',  value: equipment.length, color: 'blue' },
      { label: 'Active',           value: equipment.filter(e => e.status === 'Active').length, color: 'green' },
      { label: 'EOL (5+ yrs)',     value: equipment.filter(e => e.eol_flag).length, color: equipment.some(e => e.eol_flag) ? 'red' : 'default' },
      { label: 'Data Complete',    value: completePct(equipment.length, equipment.filter(e => !!e.brand_serial && e.year_acquired > 0 && e.dept_id !== 999).length), color: 'slate' },
    ],
    network: [
      { label: 'Total Net Devices',value: netDevices.length, color: 'blue' },
      { label: 'Active',           value: netDevices.filter(d => d.status === 'Active').length, color: 'green' },
      { label: 'Inactive',         value: netDevices.filter(d => d.status === 'Inactive').length, color: 'amber' },
      { label: 'Data Complete',    value: completePct(netDevices.length, netDevices.filter(d => !!d.brand_serial && d.dept_id !== 999).length), color: 'slate' },
    ],
  };

  return (
    <div className="space-y-4">
      {/* Sub-tab pills */}
      <div className="flex gap-2 flex-wrap">
        {subTabs.map(({ id, label, count, icon: Icon }) => (
          <button key={id} onClick={() => setSub(id)}
            className={clsx('flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all',
              sub === id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50')}>
            <Icon className="h-4 w-4" />
            {label}
            <span className={clsx('rounded-full px-1.5 py-0.5 text-xs', sub === id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600')}>{count}</span>
          </button>
        ))}
      </div>

      {/* Mini dashboard — changes with subtab */}
      <StatStrip stats={subStats[sub]} />

      {sub === 'printers' && <GenericTable rows={printers as unknown as Record<string, unknown>[]} headers={[
        { key: 'printer_id', label: 'ID' }, { key: 'brand_serial', label: 'Serial' },
        { key: 'type', label: 'Type' }, { key: 'year_acquired', label: 'Year' },
        { key: 'status', label: 'Status' }, { key: 'eol_flag', label: 'EOL' },
        { key: 'remarks', label: 'Remarks' },
      ]} />}

      {sub === 'mobile' && <GenericTable rows={mobile as unknown as Record<string, unknown>[]} headers={[
        { key: 'mobile_id', label: 'ID' }, { key: 'brand_serial', label: 'Serial' },
        { key: 'type', label: 'Type' }, { key: 'year_acquired', label: 'Year' },
        { key: 'status', label: 'Status' }, { key: 'remarks', label: 'Remarks' },
      ]} />}

      {sub === 'equipment' && <GenericTable rows={equipment as unknown as Record<string, unknown>[]} headers={[
        { key: 'equipment_id', label: 'ID' }, { key: 'brand_serial', label: 'Serial' },
        { key: 'type', label: 'Type' }, { key: 'year_acquired', label: 'Year' },
        { key: 'status', label: 'Status' }, { key: 'eol_flag', label: 'EOL' },
        { key: 'remarks', label: 'Remarks' },
      ]} />}

      {sub === 'network' && <GenericTable rows={netDevices as unknown as Record<string, unknown>[]} headers={[
        { key: 'device_id', label: 'ID' }, { key: 'brand_serial', label: 'Serial' },
        { key: 'type_text', label: 'Device Type' }, { key: 'year_acquired', label: 'Year' },
        { key: 'status', label: 'Status' }, { key: 'remarks', label: 'Remarks' },
      ]} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 4: Software & Systems
// ---------------------------------------------------------------------------
function SoftwareTab({ software, systems, m }: {
  software: ComputerSoftware[]; systems: CitySystem[]; m: IsspDashboardMetrics;
}) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() =>
    software.filter(s =>
      !q || s.office_software.toLowerCase().includes(q.toLowerCase()) ||
      (s.computer_id ?? '').toLowerCase().includes(q.toLowerCase()) ||
      s.software_license.toLowerCase().includes(q.toLowerCase())
    ), [software, q]);

  const licBadge = (l: string) => {
    const lc = l.toLowerCase();
    if (lc === 'unlicensed') return 'danger';
    if (lc === 'subscription') return 'warning';
    if (lc === 'licensed' || lc === 'oem') return 'success';
    return 'default';
  };

  const swComplete = useMemo(() => software.filter(s => !!s.office_software && s.software_license && s.software_license !== 'Unknown').length, [software]);

  return (
    <div className="space-y-6">
      {/* Mini dashboard */}
      <StatStrip stats={[
        { label: 'SW Entries',       value: software.length,  color: 'blue' },
        { label: 'Licensed + OEM',   value: m.license.licensed + m.license.oem, color: 'green' },
        { label: 'Unlicensed',       value: m.license.unlicensed, color: m.license.unlicensed > 0 ? 'red' : 'default' },
        { label: 'Subscription',     value: m.license.subscription, color: m.license.subscription > 0 ? 'amber' : 'default', sub: 'may include gaming SW' },
        { label: 'City Systems',     value: systems.length, color: 'purple' },
        { label: 'Annual OPEX',      value: PHP(m.totalOpex), color: 'purple', sub: 'system maintenance' },
        { label: 'SW Data Complete', value: completePct(software.length, swComplete), color: swComplete/Math.max(software.length,1) >= 0.8 ? 'green' : 'amber' },
      ]} />

      {/* City Systems / OPEX */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">City ICT Systems</h3>
            <p className="text-xs text-gray-500 mt-0.5">Proprietary / third-party systems with annual maintenance obligations</p>
          </div>
          <Badge variant="info">{systems.length} systems</Badge>
        </div>
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              {['ID','System Name','Developer','Platform','Mode','Utilization','Annual OPEX','Dept'].map(h =>
                <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {systems.length === 0 && <EmptyRow cols={8} />}
            {systems.map(s => (
              <tr key={s.system_id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 font-mono text-xs text-blue-600">{s.system_id}</td>
                <td className="px-4 py-2.5 font-medium text-gray-800 max-w-[180px]" title={s.system_name}>{s.system_name}</td>
                <td className="px-4 py-2.5 text-xs text-gray-600">{s.developer}</td>
                <td className="px-4 py-2.5 text-xs text-gray-600">{s.development_platform}</td>
                <td className="px-4 py-2.5 text-xs text-gray-600">{s.working_development}</td>
                <td className="px-4 py-2.5"><Badge variant="info">{s.utilization}</Badge></td>
                <td className="px-4 py-2.5 font-semibold text-purple-700">{PHP(s.maintenance_cost)}</td>
                <td className="px-4 py-2.5 text-xs text-gray-500">{getDeptAcronym(s.dept_id)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Computer Software matrix */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="border-b px-6 py-4 flex items-center gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800">Installed Software Matrix</h3>
            <p className="text-xs text-gray-500 mt-0.5">Per-device software audit — {software.length} entries across {new Set(software.map(s => s.computer_id)).size} computers</p>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <input className="rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Search software, ID, license…" value={q} onChange={e => setQ(e.target.value)} />
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              {['Computer ID','Brand / Serial','Software','License','Remarks'].map(h =>
                <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.length === 0 && <EmptyRow cols={5} />}
            {filtered.map((s, i) => (
              <tr key={i} className={clsx('hover:bg-gray-50',
                s.software_license.toLowerCase() === 'unlicensed' && 'bg-red-50/30',
                s.software_license.toLowerCase() === 'subscription' && 'bg-amber-50/30')}>
                <td className="px-4 py-2.5 font-mono text-xs text-blue-600">{s.computer_id}</td>
                <td className="px-4 py-2.5 text-xs text-gray-600 max-w-[140px] truncate" title={s.brand_serial}>{s.brand_serial}</td>
                <td className="px-4 py-2.5 text-gray-800 font-medium">{s.office_software}</td>
                <td className="px-4 py-2.5">
                  <Badge variant={licBadge(s.software_license)}>{s.software_license}</Badge>
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-500">{s.remarks || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(m.license.subscription > 0) && (
          <div className="border-t bg-amber-50 px-6 py-3 text-xs text-amber-800 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <strong>Audit Note:</strong> Gaming software (GTA San Andreas, Warcraft) detected on government computers under "Subscription" license — requires immediate review.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 5: Audit Matrix
// ---------------------------------------------------------------------------
function AuditTab({ networkSurvey, securitySurvey, m }: {
  networkSurvey: NetworkSurveyItem[]; securitySurvey: SecuritySurveyItem[];
  m: IsspDashboardMetrics;
}) {
  const isYes = (a: string) => ['yes','true','1','available','active','present','installed'].includes((a ?? '').toLowerCase().trim());

  function SurveyCard({ item, type }: { item: NetworkSurveyItem | SecuritySurveyItem; type: 'network' | 'security' }) {
    const positive = isYes(item.answer ?? '');
    const na = (item.answer ?? '').toUpperCase() === 'N/A';
    return (
      <div className={clsx('rounded-lg border p-3.5 shadow-sm',
        na ? 'border-gray-200 bg-gray-50' :
        positive ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50')}>
        <div className="flex items-start gap-2.5">
          {na ? <span className="h-5 w-5 text-gray-400 text-xs font-bold mt-0.5">N/A</span> :
           positive ? <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                    : <XCircle     className="h-5 w-5 text-red-400    shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 leading-snug">{item.question}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <Badge variant={na ? 'default' : positive ? 'success' : 'danger'}>{item.answer || 'No Data'}</Badge>
              {type === 'network' && (item as NetworkSurveyItem).units && (item as NetworkSurveyItem).units !== '-' &&
                <Badge variant="default">{(item as NetworkSurveyItem).units}</Badge>}
              <Badge variant="info">{item.category}</Badge>
            </div>
            {type === 'network' && (item as NetworkSurveyItem).remarks && (item as NetworkSurveyItem).remarks !== '-' &&
              <p className="mt-1 text-xs text-gray-500">{(item as NetworkSurveyItem).remarks}</p>}
            {type === 'security' && (item as SecuritySurveyItem).details && (item as SecuritySurveyItem).details !== '-' &&
              <p className="mt-1 text-xs text-gray-500">{(item as SecuritySurveyItem).details}</p>}
          </div>
        </div>
      </div>
    );
  }

  const passing = m.cyber.indicators.filter(i => i.present).length;

  return (
    <div className="space-y-6">
      {/* Mini dashboard */}
      <StatStrip stats={[
        { label: 'DICT Score',       value: PCT(m.cyber.percentage), color: m.cyber.percentage >= 70 ? 'green' : m.cyber.percentage >= 50 ? 'amber' : 'red', sub: m.cyber.rating },
        { label: 'Indicators Pass',  value: `${passing}/${m.cyber.indicators.length}`, color: passing === m.cyber.indicators.length ? 'green' : passing >= m.cyber.indicators.length/2 ? 'amber' : 'red' },
        { label: 'Network Items',    value: networkSurvey.length, color: 'blue' },
        { label: 'Security Items',   value: securitySurvey.length, color: 'purple' },
        { label: 'Network Positive', value: networkSurvey.filter(i => ['yes','true','1','available','active','present','installed','y'].includes((i.answer??'').toLowerCase().trim())).length, color: 'green' },
        { label: 'Security Positive',value: securitySurvey.filter(i => ['yes','true','1','available','active','present','installed','y'].includes((i.answer??'').toLowerCase().trim())).length, color: 'green' },
      ]} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <div className="mb-3 flex items-center gap-2">
            <Wifi className="h-5 w-5 text-blue-500" />
            <h3 className="font-semibold text-gray-800">Network Survey</h3>
            <Badge variant="info">{networkSurvey.length} items</Badge>
          </div>
          <div className="space-y-2">
            {networkSurvey.length === 0
              ? <p className="rounded-lg border border-dashed p-6 text-center text-sm text-gray-400">No network survey data</p>
              : networkSurvey.map((item, i) => <SurveyCard key={i} item={item} type="network" />)}
          </div>
        </div>
        <div>
          <div className="mb-3 flex items-center gap-2">
            <Lock className="h-5 w-5 text-purple-500" />
            <h3 className="font-semibold text-gray-800">Security Survey</h3>
            <Badge variant="info">{securitySurvey.length} items</Badge>
          </div>
          <div className="space-y-2">
            {securitySurvey.length === 0
              ? <p className="rounded-lg border border-dashed p-6 text-center text-sm text-gray-400">No security survey data</p>
              : securitySurvey.map((item, i) => <SurveyCard key={i} item={item} type="security" />)}
          </div>

          {/* Cyber indicator summary */}
          <div className="mt-4 rounded-xl border bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">DICT Indicator Scorecard</p>
            <div className="space-y-2">
              {m.cyber.indicators.map(ind => (
                <div key={ind.label} className={clsx('flex items-center gap-2 rounded-lg px-3 py-2',
                  ind.present ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200')}>
                  {ind.present ? <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                               : <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />}
                  <span className="flex-1 text-xs text-gray-800">{ind.label}</span>
                  <Badge variant={ind.present ? 'success' : 'danger'}>{ind.present ? 'PASS' : 'FAIL'}</Badge>
                  <span className="text-xs text-gray-400">{ind.weight}pts</span>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2.5 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">Total Score</span>
              <span className="text-sm font-bold text-gray-800">{m.cyber.score}/{m.cyber.maxScore} — {PCT(m.cyber.percentage)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 6: Department View
// ---------------------------------------------------------------------------
function DepartmentsTab({ m, computers, software, printers, mobile, equipment, netDevices, systems }: {
  m: IsspDashboardMetrics;
  computers: Computer[]; software: ComputerSoftware[]; printers: PrinterT[];
  mobile: MobileDevice[]; equipment: OtherEquipment[]; netDevices: NetworkDevice[];
  systems: CitySystem[];
}) {
  const [cluster, setCluster] = useState('All');
  const [selected, setSelected] = useState<number | null>(null);
  const [sortCol, setSortCol] = useState<keyof DeptAssetSummary>('total');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');

  const clusters = useMemo(() => ['All', ...Array.from(new Set(m.deptSummary.map(d => d.cluster))).sort()], [m.deptSummary]);

  const rows = useMemo(() => {
    const filtered = cluster === 'All' ? m.deptSummary : m.deptSummary.filter(d => d.cluster === cluster);
    return [...filtered].sort((a, b) => {
      const av = a[sortCol] as number, bv = b[sortCol] as number;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [m.deptSummary, cluster, sortCol, sortDir]);

  const selectedDept = selected != null ? m.deptSummary.find(d => d.dept_id === selected) : null;
  const deptComputers  = useMemo(() => computers.filter(c => c.dept_id === selected), [computers, selected]);
  const deptSoftware   = useMemo(() => software.filter(s => s.dept_id === selected), [software, selected]);
  const deptPrinters   = useMemo(() => printers.filter(p => p.dept_id === selected), [printers, selected]);
  const deptMobile     = useMemo(() => mobile.filter(m => m.dept_id === selected), [mobile, selected]);
  const deptEquipment  = useMemo(() => equipment.filter(e => e.dept_id === selected), [equipment, selected]);
  const deptNetDevices = useMemo(() => netDevices.filter(n => n.dept_id === selected), [netDevices, selected]);
  const deptSystems    = useMemo(() => systems.filter(s => s.dept_id === selected), [systems, selected]);

  function SortTh({ col, label }: { col: keyof DeptAssetSummary; label: string }) {
    const active = sortCol === col;
    return (
      <th className="px-3 py-3 text-right font-semibold cursor-pointer select-none hover:bg-gray-100 transition-colors"
        onClick={() => { if (active) setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setSortCol(col); setSortDir('desc'); } }}>
        {label} {active ? (sortDir === 'desc' ? '↓' : '↑') : ''}
      </th>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cluster filter */}
      <div className="flex flex-wrap gap-2">
        {clusters.map(c => (
          <button key={c} onClick={() => setCluster(c)}
            className={clsx('rounded-lg border px-3 py-1.5 text-xs font-medium transition-all',
              cluster === c ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50')}>
            {c}
          </button>
        ))}
      </div>

      {/* Department table */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="border-b px-5 py-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-800">All Departments — Asset &amp; Compliance Summary</p>
          <Badge variant="info">{rows.length} departments</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-xs">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-3 text-left font-semibold sticky left-0 bg-gray-50">Department</th>
                <th className="px-3 py-3 text-left font-semibold">Cluster</th>
                <SortTh col="computers"    label="Computers" />
                <SortTh col="win11Count"   label="Win11" />
                <SortTh col="eolComputers" label="EOL" />
                <SortTh col="eolPct"       label="EOL %" />
                <SortTh col="printers"     label="Printers" />
                <SortTh col="mobile"       label="Mobile" />
                <SortTh col="equipment"    label="Equipment" />
                <SortTh col="netDevices"   label="Network" />
                <SortTh col="total"        label="Total" />
                <SortTh col="unlicensedSw" label="Unlicensed SW" />
                <SortTh col="totalOpex"    label="OPEX" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(d => (
                <tr key={d.dept_id}
                  className={clsx('cursor-pointer transition-colors',
                    selected === d.dept_id ? 'bg-blue-50' : 'hover:bg-gray-50',
                    d.eolPct >= 80 && selected !== d.dept_id && 'bg-red-50/20')}
                  onClick={() => setSelected(selected === d.dept_id ? null : d.dept_id)}>
                  <td className="px-3 py-2.5 sticky left-0 bg-inherit">
                    <span className="font-semibold text-gray-800">{d.acronym}</span>
                    <span className="block text-[10px] text-gray-400 max-w-[140px] truncate" title={d.dept_name}>{d.dept_name}</span>
                  </td>
                  <td className="px-3 py-2.5"><Badge variant="info">{d.cluster}</Badge></td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{d.computers}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-emerald-600 font-semibold">{d.win11Count}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <span className={d.eolComputers > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}>{d.eolComputers}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <span className={clsx('font-semibold', d.eolPct >= 80 ? 'text-red-600' : d.eolPct >= 50 ? 'text-amber-600' : 'text-gray-500')}>
                      {d.eolPct}%
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{d.printers || '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{d.mobile || '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{d.equipment || '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{d.netDevices || '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-gray-800">{d.total}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <span className={d.unlicensedSw > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}>{d.unlicensedSw || '—'}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-purple-700 font-semibold">{d.totalOpex > 0 ? PHP(d.totalOpex) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Department detail panel */}
      {selectedDept && (
        <div className="rounded-xl border-2 border-blue-200 bg-blue-50/30 p-6 space-y-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{selectedDept.dept_name}</h3>
              <p className="text-sm text-gray-500">{selectedDept.acronym} · {selectedDept.cluster} · Dept ID {selectedDept.dept_id}</p>
            </div>
            <button onClick={() => setSelected(null)} className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50">
              Close ✕
            </button>
          </div>

          {/* Asset stat strip */}
          <StatStrip stats={[
            { label: 'Computers',    value: selectedDept.computers,    color: 'blue' },
            { label: 'Win11',        value: selectedDept.win11Count,   color: 'green', sub: `${selectedDept.win11Pct}% compliant` },
            { label: 'EOL',          value: selectedDept.eolComputers, color: selectedDept.eolComputers > 0 ? 'red' : 'green', sub: `${selectedDept.eolPct}%` },
            { label: 'Printers',     value: selectedDept.printers,     color: 'slate' },
            { label: 'Mobile',       value: selectedDept.mobile,       color: 'purple' },
            { label: 'Equipment',    value: selectedDept.equipment,    color: 'amber' },
            { label: 'Net Devices',  value: selectedDept.netDevices,   color: 'slate' },
            { label: 'Unlicensed SW',value: selectedDept.unlicensedSw, color: selectedDept.unlicensedSw > 0 ? 'red' : 'green' },
            { label: 'OPEX',         value: selectedDept.totalOpex > 0 ? PHP(selectedDept.totalOpex) : '₱0', color: 'purple' },
          ]} />

          {/* Computer OS breakdown */}
          {deptComputers.length > 0 && (
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Computers ({deptComputers.length})</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs divide-y divide-gray-100">
                  <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                    <tr>{['ID','Serial','Type','Year','OS','License','Status','EOL'].map(h =>
                      <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {deptComputers.slice(0, 20).map(c => (
                      <tr key={c.computer_id} className={clsx('hover:bg-gray-50', c.eol_flag && 'bg-red-50/40')}>
                        <td className="px-3 py-2 font-mono text-blue-600">{c.computer_id}</td>
                        <td className="px-3 py-2 text-gray-700 max-w-[120px] truncate">{c.brand_serial}</td>
                        <td className="px-3 py-2"><Badge variant="info">{c.type}</Badge></td>
                        <td className="px-3 py-2 text-gray-600">{c.year_acquired || '—'}</td>
                        <td className="px-3 py-2">
                          <Badge variant={osIsWin11(c.os) ? 'success' : 'danger'}>{c.os || 'Unknown'}</Badge>
                        </td>
                        <td className="px-3 py-2"><Badge variant={c.os_license === 'OEM' || c.os_license === 'ACTIVATED' || c.os_license === 'Licensed' ? 'success' : 'warning'}>{c.os_license}</Badge></td>
                        <td className="px-3 py-2"><Badge variant={c.status === 'Active' ? 'success' : c.status === 'Defective' ? 'danger' : 'warning'}>{c.status}</Badge></td>
                        <td className="px-3 py-2">{c.eol_flag ? <span className="text-red-600 font-semibold">EOL</span> : <span className="text-emerald-500">OK</span>}</td>
                      </tr>
                    ))}
                    {deptComputers.length > 20 && (
                      <tr><td colSpan={8} className="px-3 py-2 text-center text-gray-400">+ {deptComputers.length - 20} more — use Computers tab to see all</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Systems */}
          {deptSystems.length > 0 && (
            <div className="rounded-xl border bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">ICT Systems ({deptSystems.length})</p>
              <div className="space-y-1.5">
                {deptSystems.map(s => (
                  <div key={s.system_id} className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2 text-xs">
                    <span className="flex-1 font-medium text-gray-800">{s.system_name}</span>
                    <Badge variant="info">{s.utilization}</Badge>
                    {s.maintenance_cost > 0 && <span className="font-semibold text-purple-700">{PHP(s.maintenance_cost)}/yr</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unlicensed software */}
          {deptSoftware.filter(s => s.software_license.toLowerCase() === 'unlicensed').length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-2">Unlicensed Software</p>
              <div className="space-y-1">
                {deptSoftware.filter(s => s.software_license.toLowerCase() === 'unlicensed').map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-red-700">
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    <span className="font-medium">{s.office_software}</span>
                    <span className="text-red-400">— Computer {s.computer_id || 'unlinked'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 7: Database & Sync
// ---------------------------------------------------------------------------
function SyncTab({ mode, logs, onIngest, onSyncD1, syncing, onClearAll }: {
  mode: StorageMode; logs: SyncLog[];
  onIngest: (f: File) => Promise<void>;
  onSyncD1: () => Promise<void>;
  syncing: boolean;
  onClearAll: () => Promise<void>;
}) {
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [msg, setMsg] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    if (!file.name.endsWith('.csv')) { setMsg('Error: Please upload a .csv file.'); return; }
    setProcessing(true); setMsg('Parsing inventory CSV…');
    try { await onIngest(file); setMsg(`Ingested: ${file.name}`); }
    catch (e) { setMsg(`Error: ${String(e)}`); }
    finally { setProcessing(false); }
  }

  return (
    <div className="space-y-6">
      {/* Mode badge */}
      <div className={clsx('flex items-center gap-4 rounded-xl border p-5 shadow-sm',
        mode.mode === 'd1' ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50')}>
        <Database className={clsx('h-8 w-8', mode.mode === 'd1' ? 'text-blue-600' : 'text-amber-600')} />
        <div className="flex-1">
          <p className="font-semibold text-gray-800">{mode.label}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {mode.mode === 'indexeddb'
              ? 'Dev mode — all data persists in your browser IndexedDB only.'
              : 'Production mode — reads/writes go to Cloudflare D1 edge SQL.'}
          </p>
        </div>
        <Badge variant={mode.mode === 'd1' ? 'success' : 'warning'}>
          {mode.mode === 'd1' ? 'PROD · D1' : 'DEV · IDB'}
        </Badge>
      </div>

      {/* Drop zone */}
      <div onDragOver={e => { e.preventDefault(); setDragging(true); }}
           onDragLeave={() => setDragging(false)}
           onDrop={(e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
           onClick={() => inputRef.current?.click()}
           className={clsx('cursor-pointer rounded-xl border-2 border-dashed p-12 text-center transition-all',
             dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50')}>
        <Upload className="mx-auto h-10 w-10 text-gray-400" />
        <p className="mt-3 text-base font-medium text-gray-700">Drop inventory_report_all_csv.csv here</p>
        <p className="mt-1 text-sm text-gray-400">9-section stacked CSV accepted — PHP warnings auto-scrubbed</p>
        <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={(e: ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
      </div>

      {msg && (
        <div className={clsx('rounded-lg px-4 py-3 text-sm border',
          msg.startsWith('Error') ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200')}>
          {msg.startsWith('Error') ? <AlertTriangle className="inline h-4 w-4 mr-1" /> : <CheckCircle className="inline h-4 w-4 mr-1" />}
          {msg}
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {mode.mode === 'indexeddb' && (
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-1">Sync to D1</h3>
            <p className="text-xs text-gray-500 mb-4">Push local data to Cloudflare D1 (requires production deployment).</p>
            <button onClick={onSyncD1} disabled={syncing}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
              <RefreshCw className={clsx('h-4 w-4', syncing && 'animate-spin')} />
              {syncing ? 'Syncing…' : 'Trigger Manual Sync'}
            </button>
          </div>
        )}
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-1">Clear All Data</h3>
          <p className="text-xs text-gray-500 mb-4">Wipe all local storage and re-upload a fresh CSV.</p>
          <button onClick={onClearAll}
            className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-100 transition-colors">
            <XCircle className="h-4 w-4" /> Clear All Storage
          </button>
        </div>
      </div>

      {/* Sync log */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="border-b px-6 py-4"><h3 className="font-semibold text-gray-800">Ingest / Sync Log</h3></div>
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>{['Sync ID','File','Computers','Software','Printers','Mobile','Equipment','Status','When'].map(h =>
              <th key={h} className="px-3 py-3 text-left font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {logs.length === 0 && <EmptyRow cols={9} />}
            {logs.map(l => (
              <tr key={l.sync_id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 font-mono text-xs text-blue-600 truncate max-w-[120px]">{l.sync_id}</td>
                <td className="px-3 py-2.5 text-gray-700 text-xs">{l.filename}</td>
                <td className="px-3 py-2.5 text-center text-gray-600">{l.computers_n}</td>
                <td className="px-3 py-2.5 text-center text-gray-600">{l.software_n}</td>
                <td className="px-3 py-2.5 text-center text-gray-600">{l.printers_n}</td>
                <td className="px-3 py-2.5 text-center text-gray-600">{l.mobile_n}</td>
                <td className="px-3 py-2.5 text-center text-gray-600">{l.equipment_n}</td>
                <td className="px-3 py-2.5"><Badge variant={l.status === 'success' ? 'success' : l.status === 'partial' ? 'warning' : 'danger'}>{l.status}</Badge></td>
                <td className="px-3 py-2.5 text-xs text-gray-400">{new Date(l.synced_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root App
// ---------------------------------------------------------------------------
const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'dashboard',   label: 'Dashboard',        icon: BarChart3 },
  { id: 'computers',   label: 'Computers',         icon: Cpu },
  { id: 'assets',      label: 'Other Assets',      icon: HardDrive },
  { id: 'software',    label: 'Software & Systems', icon: Package },
  { id: 'audit',       label: 'Audit Matrix',      icon: ShieldCheck },
  { id: 'departments', label: 'Departments',        icon: Building2 },
  { id: 'sync',        label: 'Database & Sync',   icon: Database },
];

const EMPTY_METRICS: IsspDashboardMetrics = {
  eol: { totalComputers: 0, eolCount: 0, eolPercentage: 0, eolByDept: {}, refreshCostProjection: { 10: 0, 50: 0, 100: 0 } },
  license: { licensed: 0, unlicensed: 0, oem: 0, subscription: 0, freeware: 0, unknown: 0, flaggedEntries: [] },
  opex: [], totalOpex: 0,
  cyber: { score: 0, maxScore: 100, percentage: 0, rating: 'Critical', indicators: [] },
  totalComputers: 0, totalPrinters: 0, totalMobile: 0, totalEquipment: 0,
  totalNetDevices: 0, totalFleet: 0, activeComputers: 0,
  osDistribution: [], ageDistribution: [], deptSummary: [],
  win11Count: 0, win11Pct: 0,
  refreshQueue: [], opexByCluster: [],
  win11Funnel: { total: 0, win11: 0, nonWin11: 0, upgradeable: 0, mustReplace: 0 },
};

export default function App() {
  const [tab, setTab]                   = useState<TabId>('dashboard');
  const [computers, setComputers]       = useState<Computer[]>([]);
  const [software, setSoftware]         = useState<ComputerSoftware[]>([]);
  const [printers, setPrinters]         = useState<PrinterT[]>([]);
  const [mobile, setMobile]             = useState<MobileDevice[]>([]);
  const [equipment, setEquipment]       = useState<OtherEquipment[]>([]);
  const [netDevices, setNetDevices]     = useState<NetworkDevice[]>([]);
  const [systems, setSystems]           = useState<CitySystem[]>([]);
  const [networkSurvey, setNetworkSurvey]   = useState<NetworkSurveyItem[]>([]);
  const [securitySurvey, setSecuritySurvey] = useState<SecuritySurveyItem[]>([]);
  const [syncLogs, setSyncLogs]         = useState<SyncLog[]>([]);
  const [loading, setLoading]           = useState(true);
  const [syncing, setSyncing]           = useState(false);

  const repo    = getRepository();
  const storageMode: StorageMode = getStorageMode();

  // Load from storage on mount; seed if empty
  useEffect(() => {
    (async () => {
      try {
        const [c, sw, p, m, eq, nd, sys, ns, ss, logs] = await Promise.all([
          repo.getComputers(), repo.getComputerSoftware(), repo.getPrinters(),
          repo.getMobileDevices(), repo.getOtherEquipment(), repo.getNetworkDevices(),
          repo.getSystems(), repo.getNetworkSurvey(), repo.getSecuritySurvey(), repo.getSyncLogs(),
        ]);

        if (c.length === 0) {
          // First launch — seed with the uploaded CSV data.
          // computers must be saved before computer_software (FK constraint).
          await repo.saveComputers(SEED_COMPUTERS);
          await repo.saveComputerSoftware(SEED_SOFTWARE);
          await Promise.all([
            repo.savePrinters(SEED_PRINTERS),
            repo.saveMobileDevices(SEED_MOBILE),
            repo.saveOtherEquipment(SEED_EQUIPMENT),
            repo.saveNetworkDevices(SEED_NET_DEVICES),
            repo.saveSystems(SEED_SYSTEMS),
            repo.saveNetworkSurvey(SEED_NETWORK_SURVEY),
            repo.saveSecuritySurvey(SEED_SECURITY_SURVEY),
          ]);
          const seedLog: SyncLog = {
            sync_id: uid(), filename: 'inventory_report_all_csv.csv (seed)',
            computers_n: SEED_COMPUTERS.length, software_n: SEED_SOFTWARE.length,
            printers_n: SEED_PRINTERS.length, mobile_n: SEED_MOBILE.length,
            equipment_n: SEED_EQUIPMENT.length, netdevices_n: SEED_NET_DEVICES.length,
            systems_n: SEED_SYSTEMS.length, network_n: SEED_NETWORK_SURVEY.length,
            security_n: SEED_SECURITY_SURVEY.length,
            eol_count: SEED_COMPUTERS.filter(c => c.eol_flag).length,
            win11_count: SEED_COMPUTERS.filter(c => osIsWin11(c.os)).length,
            status: 'success', synced_at: new Date().toISOString(),
          };
          await repo.syncReport(seedLog);
          setComputers(SEED_COMPUTERS); setSoftware(SEED_SOFTWARE); setPrinters(SEED_PRINTERS);
          setMobile(SEED_MOBILE); setEquipment(SEED_EQUIPMENT); setNetDevices(SEED_NET_DEVICES);
          setSystems(SEED_SYSTEMS); setNetworkSurvey(SEED_NETWORK_SURVEY);
          setSecuritySurvey(SEED_SECURITY_SURVEY); setSyncLogs([seedLog]);
        } else {
          setComputers(c); setSoftware(sw); setPrinters(p); setMobile(m);
          setEquipment(eq); setNetDevices(nd); setSystems(sys);
          setNetworkSurvey(ns); setSecuritySurvey(ss); setSyncLogs(logs);
        }
      } catch (e) { console.error('Storage load error:', e); }
      finally { setLoading(false); }
    })();
  }, []);

  const metrics = useMemo(
    () => computers.length > 0
      ? computeIsspMetrics(computers, software, printers, mobile, equipment, netDevices, systems, networkSurvey, securitySurvey)
      : EMPTY_METRICS,
    [computers, software, printers, mobile, equipment, netDevices, systems, networkSurvey, securitySurvey]
  );

  const handleIngest = useCallback(async (file: File) => {
    const text = await file.text();
    const parsed = parseInventoryCSV(text);

    const c   = normalizeComputers(parsed.computers);
    const sw  = normalizeComputerSoftware(parsed.computerSoftware, c);
    const p   = normalizePrinters(parsed.printers);
    const m   = normalizeMobileDevices(parsed.mobileDevices);
    const eq  = normalizeOtherEquipment(parsed.otherEquipment);
    const nd  = normalizeNetworkDevices(parsed.networkDevices);
    const sys = normalizeSystems(parsed.systems);
    const ns  = normalizeNetworkSurvey(parsed.networkSurvey);
    const ss  = normalizeSecuritySurvey(parsed.securitySurvey);

    // computers must complete before software (FK constraint in D1)
    await repo.saveComputers(c);
    await repo.saveComputerSoftware(sw);
    await Promise.all([
      repo.savePrinters(p), repo.saveMobileDevices(m), repo.saveOtherEquipment(eq),
      repo.saveNetworkDevices(nd), repo.saveSystems(sys),
      repo.saveNetworkSurvey(ns), repo.saveSecuritySurvey(ss),
    ]);

    const log: SyncLog = {
      sync_id: uid(), filename: file.name,
      computers_n: c.length, software_n: sw.length, printers_n: p.length,
      mobile_n: m.length, equipment_n: eq.length, netdevices_n: nd.length,
      systems_n: sys.length, network_n: ns.length, security_n: ss.length,
      eol_count: c.filter(x => x.eol_flag).length,
      win11_count: c.filter(x => osIsWin11(x.os)).length,
      status: parsed.parseErrors.length === 0 ? 'success' : 'partial',
      error_msg: parsed.parseErrors.join('; ') || undefined,
      synced_at: new Date().toISOString(),
    };
    await repo.syncReport(log);

    setComputers(c); setSoftware(sw); setPrinters(p); setMobile(m);
    setEquipment(eq); setNetDevices(nd); setSystems(sys);
    setNetworkSurvey(ns); setSecuritySurvey(ss);
    setSyncLogs(prev => [log, ...prev]);

    if (parsed.parseErrors.length > 0) throw new Error(parsed.parseErrors.join('; '));
  }, [repo]);

  const handleSyncD1 = useCallback(async () => {
    setSyncing(true);
    try {
      const log: SyncLog = {
        sync_id: uid(), filename: 'manual-d1-sync',
        computers_n: computers.length, software_n: software.length,
        printers_n: printers.length, mobile_n: mobile.length,
        equipment_n: equipment.length, netdevices_n: netDevices.length,
        systems_n: systems.length, network_n: networkSurvey.length,
        security_n: securitySurvey.length,
        eol_count: computers.filter(c => c.eol_flag).length,
        win11_count: computers.filter(c => osIsWin11(c.os)).length,
        status: 'success',
        synced_at: new Date().toISOString(),
      };
      const payload: BulkUpsertPayload = {
        computers, computerSoftware: software, printers, mobileDevices: mobile,
        otherEquipment: equipment, networkDevices: netDevices, systems,
        networkSurvey, securitySurvey, sync_log: log,
      };
      await bulkSyncToD1(payload);
      setSyncLogs(prev => [log, ...prev]);
    } catch (e) { console.error('D1 sync failed:', e); }
    finally { setSyncing(false); }
  }, [computers, software, printers, mobile, equipment, netDevices, systems, networkSurvey, securitySurvey]);

  const handleClearAll = useCallback(async () => {
    if (!confirm('Clear all stored data? This cannot be undone.')) return;
    await repo.clearAll();
    setComputers([]); setSoftware([]); setPrinters([]); setMobile([]);
    setEquipment([]); setNetDevices([]); setSystems([]);
    setNetworkSurvey([]); setSecuritySurvey([]); setSyncLogs([]);
  }, [repo]);

  if (loading) return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <RefreshCw className="mx-auto h-8 w-8 animate-spin text-blue-500" />
        <p className="mt-3 text-sm text-gray-500">Loading DAMS…</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-700 p-2">
                <Database className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 leading-none">DAMS</p>
                <p className="text-xs text-gray-500">DASMO Asset Management System · Iloilo City Government</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={clsx('hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium',
                storageMode.mode === 'd1' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700')}>
                <span className={clsx('h-1.5 w-1.5 rounded-full', storageMode.mode === 'd1' ? 'bg-blue-500' : 'bg-amber-500')} />
                {storageMode.label}
              </span>
              <span className="text-xs text-gray-400 hidden md:block">BAC-S · Dept 131</span>
            </div>
          </div>
          {/* Tabs */}
          <nav className="-mb-px flex gap-0.5 overflow-x-auto">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setTab(id)}
                className={clsx('flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors',
                  tab === id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700')}>
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        {tab === 'dashboard'   && <DashboardTab m={metrics} syncLogs={syncLogs} onGoToSync={() => setTab('sync')} />}
        {tab === 'computers'   && <ComputersTab computers={computers} />}
        {tab === 'assets'      && <OtherAssetsTab printers={printers} mobile={mobile} equipment={equipment} netDevices={netDevices} />}
        {tab === 'software'    && <SoftwareTab software={software} systems={systems} m={metrics} />}
        {tab === 'audit'       && <AuditTab networkSurvey={networkSurvey} securitySurvey={securitySurvey} m={metrics} />}
        {tab === 'departments' && <DepartmentsTab m={metrics} computers={computers} software={software}
            printers={printers} mobile={mobile} equipment={equipment} netDevices={netDevices} systems={systems} />}
        {tab === 'sync'      && (
          <SyncTab mode={storageMode} logs={syncLogs}
            onIngest={handleIngest} onSyncD1={handleSyncD1}
            syncing={syncing} onClearAll={handleClearAll} />
        )}
      </main>

      <footer className="mt-8 border-t bg-white py-4 text-center text-xs text-gray-400">
        DAMS — DASMO Asset Management System · Iloilo City Government · DICT ISSP Compliant ·{' '}
        Cloudflare Pages + D1 © {new Date().getFullYear()}
      </footer>
    </div>
  );
}
